import React from 'react';

interface LinkExpiredPageProps {
  hospitalId?: string;
  logoUrl?: string | null;
}

/**
 * Get hospital logo path based on hospital_id and logo_url
 * 
 * Priority:
 * 1. Use logo_url from API response if available
 * 2. Try known hospital mappings (SCUB, SJBM)
 * 3. Fallback to default AXA Medis logo
 */
const getHospitalLogo = (hospitalId: string | undefined, logoUrl?: string | null): string => {
  // Priority 1: Use logo_url from API response
  if (logoUrl) {
    return logoUrl;
  }
  
  if (!hospitalId) {
    return '/assets/logo/logo.png';
  }
  
  // Priority 2: Map of known hospital IDs to their specific logo filenames
  const hospitalLogos: Record<string, string> = {
    // SCUB - Spitalul Clinic de Urgență București
    '827bd4c6-79f6-410f-bbf5-20759ea44bd7': '/assets/logo/scub.png',
    // SJBM - Spitalul Județean de Urgență Baia Mare
    'c3043559-9ddc-4e08-ae7f-1fb58d6aa6b3': '/assets/logo/sjbm_logo.png',
  };
  
  // Priority 3: Fallback to default
  return hospitalLogos[hospitalId] || '/assets/logo/logo.png';
};

const LinkExpiredPage: React.FC<LinkExpiredPageProps> = ({ hospitalId, logoUrl }) => {
  const logoSrc = getHospitalLogo(hospitalId, logoUrl);

  return (
    <div className="min-h-screen bg-slate-50 font-sans flex items-center justify-center p-4 sm:p-6">
      <div className="relative px-4 pb-12 pt-24 w-full max-w-lg">
        
        {/* --- 1. HEADER (Matches Main & Link Used Page) --- */}
        <div className="absolute top-0 left-0 w-full h-80 bg-blue-600 overflow-hidden z-0 rounded-[2rem] [mask-image:linear-gradient(to_bottom,black_50%,transparent_100%)]">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-700" />
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-400 rounded-full mix-blend-overlay filter blur-3xl opacity-20" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-40" />
          
          {/* Subtle Decorative Elements */}
          <div className="absolute top-8 left-8 transform -rotate-12 opacity-10">
            <svg className="w-[120px] h-[120px] text-white" strokeWidth={4} viewBox="0 0 24 24" fill="none" stroke="currentColor">
               <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </div>
        </div>

        {/* --- 2. MAIN CARD --- */}
        <div className="mx-auto bg-white rounded-[2rem] shadow-2xl shadow-slate-900/10 border border-slate-100 relative z-10">
          
          {/* --- FLOATING BADGE (Hospital Logo) --- */}
          <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 z-20">
            <div className="w-36 h-36 sm:w-40 sm:h-40 bg-white rounded-full shadow-xl p-2 flex items-center justify-center">
              <div className="w-full h-full bg-slate-50 rounded-full border border-slate-100 flex items-center justify-center p-4 overflow-hidden">
                <img 
                  src={logoSrc}
                  alt="Hospital Logo" 
                  className="max-w-full max-h-full w-auto h-auto object-contain"
                />
              </div>
            </div>
          </div>

          {/* --- CONTENT --- */}
          <div className="pt-24 pb-8 px-6 sm:px-8 text-center relative">
            
            {/* Icon: Clock / Expired (Rose/Red Theme) */}
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center ring-8 ring-rose-50/50">
                <svg className="w-10 h-10 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-3 tracking-tight">
              Link Expirat
            </h1>
            
            {/* Subheading */}
            <p className="text-slate-500 text-sm sm:text-base leading-relaxed mb-8 px-2">
              Termenul de valabilitate al acestui link de feedback a expirat.
            </p>

            {/* Info Box */}
            <div className="bg-rose-50/50 rounded-2xl p-5 border border-rose-100 mb-8 text-left">
              <div className="flex items-start gap-3">
                <div className="bg-rose-100 p-2 rounded-lg shrink-0">
                  <svg className="w-5 h-5 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-slate-800 text-sm mb-1">Perioadă limitată</h4>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Link-urile de feedback sunt active pentru o perioadă limitată (de obicei 48-72 de ore) de la momentul emiterii, din motive de securitate.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer / Exit Instruction */}
            <div className="border-t border-slate-100 pt-6 mt-2">
              <p className="text-slate-400 text-xs flex items-center justify-center gap-1.5">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Puteți închide această pagină.
              </p>
            </div>

          </div>
        </div>
        
        {/* Copyright Footer */}
        <p className="text-center text-slate-400/80 text-xs mt-8 relative z-10">
          © 2025 AXA Medis.
        </p>
      </div>
    </div>
  );
};

export default LinkExpiredPage;
